/**
 * JS-side mirrors of the canonical theme tokens defined in
 * `src/styles/tokens.css`.
 *
 * Use these constants whenever a hex string is required at runtime — e.g.
 * branding fallbacks that get persisted to the API, or chart fill props
 * that need a literal color. Anything that ends up in a `className` or
 * `style` prop should reference the CSS variables directly via `var(--…)`
 * instead.
 *
 * Keeping a single source means a theme swap is two coordinated edits:
 * this file and `tokens.css`. Components never re-declare colors.
 */

export const THEME = {
  /** Content surfaces (light canvas, white cards) */
  bg: '#F4F4F1',
  bgDeep: '#EBEBE6',
  surface: '#FFFFFF',
  surface2: '#FAFAF8',
  border: '#E3E3DC',

  /** App frame (sidebar / mobile nav / right rail) — dark chrome */
  chromeBg: '#0E0E0E',
  chromeBgElevated: '#1C1C1C',
  chromeBorder: '#292926',
  chromeFg: '#F5F5F3',

  /**
   * Ink accent — lands on text, icons and borders, so it must hold 4.5:1
   * against the light canvas. Deep olive, NOT bright lime. For filled
   * buttons use `lime` below with dark text on top.
   *
   * Same hue and saturation as the canonical lime (H 69.6deg, S 77.7%), only
   * darker — every green in the app is a lightness step off #C8E530.
   * #5F6F0E measures 5.58:1 on white.
   */
  accent: '#5F6F0E',
  accentHover: '#4E5B0B',
  accentDim: '#94AC16',

  /** Xotski lime — primary CTA fill + success. Always with dark text on top. */
  lime: '#C8E530',
  limeHover: '#D3EA5A',
  limeDim: '#B3D01A',

  /** Brand foreground (on the light canvas) */
  lavender: '#14140F',
  textMuted: '#5C5C55',
  textSubtle: '#8A8A80',

  /** Navy backdrop family (auth, onboarding, modals) */
  navy: '#1A1A2E',
  navy2: '#2A2A4E',
  navyMid: '#0F0F1F',

  /** Status palette */
  success: '#4DD28B',
  warn: '#F4B740',
  error: '#FF5A4F',
  info: '#5AA9FF',
} as const;

/**
 * Defaults used as fallbacks when a tenant brand kit hasn't customized a
 * field yet.
 *
 * These are CONSUMER-facing (the guest phone game and the operator view), not
 * admin chrome, so they are deliberately spelled out rather than derived from
 * `THEME`. The admin moved to a light canvas; the guest game stays on its dark
 * stage, and re-pointing these at `THEME.bg` would have flipped every
 * un-customized game to a white background.
 *
 * Only the accent changed here: the old default was Xamplin purple, now it is
 * Xotski lime. Brands that set their own colors are unaffected.
 */
export const BRAND_DEFAULTS = {
  primaryColor: THEME.navy,
  secondaryColor: THEME.lime,
  accentColor: THEME.lime,
  backgroundColor: '#0A0A0A',
  fontColor: '#F5F5F3',
  font: 'Roboto',
} as const;
